import React from 'react'
import styled from 'styled-components'

import Image from '@/components/image'
import PrismicLink from '@/components/prismic-link'
import {tokens, radii} from '@/theme'

import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import type {ImageSource} from '@/types/image'

const PADDING = 80
const VIEWBOX = 300
const RADIUS = VIEWBOX / 2

interface Props {
    ctaLink?: PrismicLinkProps
    ctaText?: string
    image: ImageSource
    mirrored?: boolean
}

const CircularImageWithCta = ({ctaLink, ctaText, image, mirrored = false}: Props) => {
    return (
        <Container>
            <ImageContainer>
                {image && <StyledImage image={image} width={600} objectFit="cover" />}

                {/* 
                    To draw curved text around the image, we create an SVG circle which is larger
                    than the image by a fixed 80px which is set in the ImageContainer styles below. We then 
                    add textPath linked to the SVG circle by #circle
                */}
                {ctaLink && ctaText && (
                    <StyledSVG viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}>
                        <defs>
                            <path
                                id="curve"
                                d={`M0 ${RADIUS}A ${RADIUS} ${RADIUS} 0 1 0 ${RADIUS} 0`}
                            />
                        </defs>

                        <StyledPrismicLink {...ctaLink}>
                            <text>
                                {/* 
                                    startOffset is the % around the curve the text starts, with the starting
                                    point being 0 on the X-axis, continuing anti-clockwise
                                */}
                                <textPath xlinkHref="#curve" startOffset={mirrored ? '45%' : '18%'}>
                                    {ctaText} →
                                </textPath>
                            </text>
                        </StyledPrismicLink>
                    </StyledSVG>
                )}
            </ImageContainer>
        </Container>
    )
}

export default CircularImageWithCta

const Container = styled.div`
    position: relative;
    display: grid;
    width: 100%;
    height: 100%;
    justify-items: center;
    align-items: center;
`

const StyledImage = styled(Image)`
    border-radius: ${radii.circle};
    object-fit: cover;
    width: 100%;
    height: 100%;
    overflow: hidden;
`

const ImageContainer = styled.div`
    position: relative;
    aspect-ratio: 1;
    width: calc(100% - ${PADDING}px);
`

const StyledSVG = styled.svg`
    position: absolute;
    top: -${PADDING / 2}px;
    left: -${PADDING / 2}px;
    width: calc(100% + ${PADDING}px);
    height: calc(100% + ${PADDING}px);
`

const StyledPrismicLink = styled(PrismicLink)`
    &:hover,
    &:focus,
    &:active {
        text {
            fill: ${tokens.pink700};
            transition: fill ${tokens.fastIn};
        }
    }
`
